/**
 * The selected workspace id, written to BOTH channels that read it.
 *
 * There are two, and they are not interchangeable:
 *  - `localStorage` — read by the browser API client (`lib/api/client.ts`
 *    `getWorkspaceId()`), which attaches it as `x-workspace-id`.
 *  - the `hf-app:workspace-id` COOKIE — the only one of the two a Next.js route
 *    handler or server component can read (`lib/phishtrainer/backend.ts`
 *    `adminAuth()`, and the LMS SCORM routes).
 *
 * Three separate call sites used to write this, and only ONE of them (login)
 * wrote the cookie. So after switching workspace, the client half of a page
 * followed the switch and the server half stayed on the login-time workspace —
 * the same screen reading two different tenants. Routing every write through
 * here is what stops them drifting apart again.
 *
 * Safe to call on the server: both writes are no-ops without `window`.
 */
export const WORKSPACE_ID_STORAGE_KEY = 'hf-app:workspace-id';
const KEY = WORKSPACE_ID_STORAGE_KEY;

export function persistWorkspaceId(workspaceId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, workspaceId);
  } catch {
    // Private mode / quota. The cookie below is the one the server needs.
  }
  document.cookie = `${KEY}=${workspaceId}; path=/; max-age=86400; SameSite=Lax`;
}

/** The id `lib/api/client.ts` sends as `x-workspace-id`, or null when there is none to send. */
export function readPersistedWorkspaceId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function clearWorkspaceId(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  document.cookie = `${KEY}=; path=/; max-age=0`;
}
